import {
    Box,
    Button,
    Divider,
    IconButton,
    Skeleton,
    Stack,
    Tab,
    Tabs,
    Typography,
} from '@mui/material'
import { FC, useEffect, useMemo, useState } from 'react'
import useInfiniteScroll from 'react-infinite-scroll-hook'
import { useNavigate } from 'react-router-dom'
import {
    useReadAllApiV1NotificationsFeedUserEventReadAllPatch,
    UserNotification,
} from '../../../api/gen/base'
import { useInfiniteNotificationsFeedUserEventListQuery } from '../../../api/hooks/useInfiniteNotificationsFeedUserEventListQuery'
import { useInvalidateNotificationsFeedUserEventListQuery } from '../../../api/hooks/useInvalidateNotificationsFeedUserEventListQuery'
import { useInvalidateNotificationsFeedUserEventTotal } from '../../../api/hooks/useInvalidateNotificationsFeedUserEventTotal'
import BellIcon from '../../../assets/icons/Bell.svg?react'
import CloseCircleIcon from '../../../assets/icons/CloseCircle.svg?react'
import GearIcon from '../../../assets/icons/Gear.svg'
import { NOTIFICATIONS_ROUTE } from '../../../constants/routing.constants'
import { useNotificationsStore } from '../../../store/storeNotifications'
import { useWrapperStore } from '../../../store/storeWrapper'
import { SidebarType } from '../../../types/sidebars'
import { NotificationListItem } from '../../NotificationListItem'
import { RightSidebarWrapper } from '../RightSidebarWrapper'

export const NotificationsSidebar: FC = () => {
    const navigate = useNavigate()
    const invalidateNotificationsListQueries = useInvalidateNotificationsFeedUserEventListQuery()
    const invalidateNotificationsTotalQueries = useInvalidateNotificationsFeedUserEventTotal()

    const { setOpenSidebarType } = useWrapperStore((state) => state)
    const { numberOfUnreadNotifications } = useNotificationsStore((state) => state)

    const [tab, setTab] = useState(1)

    const {
        data,
        isFetching,
        hasNextPage,
        fetchNextPage,
        isFetchedAfterMount,
        isFetchingNextPage,
    } = useInfiniteNotificationsFeedUserEventListQuery({
        is_read: tab === 0 ? undefined : tab !== 1,
        sort_by: 'DESC',
    })

    const {
        isSuccess: isSuccessReadAll,
        mutate: mutateReadAll,
        isPending: isPendingReadAll,
    } = useReadAllApiV1NotificationsFeedUserEventReadAllPatch()

    const [infiniteRef] = useInfiniteScroll({
        loading: isFetching,
        hasNextPage,
        onLoadMore: fetchNextPage,
    })

    const handleClose = () => {
        setOpenSidebarType(null)
    }

    const handleClickTab = (event: React.SyntheticEvent, newValue: number) => {
        setTab(newValue)
    }

    const handleOpenNotificationsPage = () => {
        navigate(NOTIFICATIONS_ROUTE)
        handleClose()
    }

    const handleOpenSettings = () => {
        setOpenSidebarType(SidebarType.NOTIFICATIONS_SETTINGS)
    }

    const handleReadAllNotifications = () => {
        mutateReadAll({})
    }

    const hasUnreadNotifications = numberOfUnreadNotifications > 0
    const shouldShowReadAllButton = hasUnreadNotifications && tab !== 2

    const tabs = useMemo(
        () => [
            {
                id: '0',
                label: <Typography variant="body2">Все</Typography>,
            },
            {
                id: '1',
                label: (
                    <Stack gap={1} direction="row" alignItems="center">
                        {hasUnreadNotifications && (
                            <Box
                                sx={{
                                    width: '8px',
                                    height: '8px',
                                    backgroundColor: '#EF4848',
                                    borderRadius: '50%',
                                }}
                            />
                        )}
                        <Typography variant="body2">Новые</Typography>
                    </Stack>
                ),
            },
            {
                id: '2',
                label: <Typography variant="body2">Прочитанные</Typography>,
            },
        ],
        [hasUnreadNotifications],
    )

    useEffect(() => {
        if (isSuccessReadAll) {
            invalidateNotificationsListQueries()
            invalidateNotificationsTotalQueries()
            handleClose()
        }
    }, [isSuccessReadAll])

    const notificationsList = useMemo(
        () =>
            data?.pages.reduce<UserNotification[]>((acc, page) => acc.concat(page.objects), []) ??
            [],
        [data],
    )

    return (
        <RightSidebarWrapper>
            <Stack
                sx={{
                    height: '100%',
                }}>
                <Stack
                    sx={{
                        flexDirection: 'row',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        padding: '16px 16px 16px 24px',
                    }}>
                    <Typography variant="h5">Уведомления</Typography>
                    <Stack
                        sx={{
                            flexDirection: 'row',
                            alignItems: 'center',
                            gap: 1,
                        }}>
                        <IconButton
                            size="large"
                            sx={{
                                backgroundColor: '#F9FAFB',
                            }}
                            onClick={handleOpenSettings}>
                            <GearIcon />
                        </IconButton>
                        <IconButton size="large" sx={{ p: 0 }} onClick={handleClose}>
                            <CloseCircleIcon />
                        </IconButton>
                    </Stack>
                </Stack>
                <Divider
                    sx={{
                        borderColor: '#F4F4F6',
                    }}
                />
                <Stack
                    sx={{
                        flexDirection: 'row',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        padding: '16px 16px 16px 8px',
                        gap: 1,
                    }}>
                    <Tabs
                        value={tab}
                        onChange={handleClickTab}
                        sx={{
                            minHeight: '36px',
                            borderBottom: 'none',
                            p: 0,
                            'button:not(.Mui-selected)': {
                                '.MuiTypography-root': {
                                    color: '#5E626E !important',
                                },
                            },
                        }}>
                        {tabs.map(({ label, id }) => (
                            <Tab key={id} id={id} variant="secondary" label={label} />
                        ))}
                    </Tabs>
                </Stack>
                <Stack
                    sx={{
                        height: '100%',
                        overflowY: 'auto',
                    }}>
                    {isFetching && !isFetchedAfterMount && !isFetchingNextPage ? (
                        <Stack
                            sx={{
                                padding: '0 16px 16px 16px',
                                gap: 1,
                            }}>
                            {Array.from({ length: 3 }).map((_, index) => (
                                <Skeleton
                                    key={`placeholder-${index}`}
                                    variant="rectangular"
                                    width="100%"
                                    height={100}
                                    sx={{ mb: '4px', py: '12px', borderRadius: '16px' }}
                                />
                            ))}
                        </Stack>
                    ) : (
                        <>
                            {!notificationsList.length ? (
                                <Stack
                                    direction="column"
                                    alignItems="center"
                                    justifyContent="center"
                                    textAlign="center"
                                    gap={2}
                                    height="100%"
                                    pb="52px">
                                    <Box
                                        sx={{
                                            width: 'fit-content',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            background: '#F9FAFB',
                                            padding: '16px',
                                            borderRadius: '10px',
                                        }}>
                                        <BellIcon />
                                    </Box>
                                    <Typography variant="h6" sx={{ textAlign: 'center' }}>
                                        У вас нет уведомлений
                                    </Typography>
                                </Stack>
                            ) : (
                                <Stack
                                    sx={{
                                        padding: '0 16px 16px 16px',
                                        gap: 1,
                                    }}>
                                    {notificationsList.map((notification) => (
                                        <NotificationListItem
                                            key={notification.id}
                                            notification={notification}
                                            onClick={handleClose}
                                            compact
                                        />
                                    ))}
                                    {hasNextPage && (
                                        <Skeleton
                                            ref={infiniteRef}
                                            variant="rectangular"
                                            width="100%"
                                            height={100}
                                            sx={{
                                                mb: '4px',
                                                py: '12px',
                                                borderRadius: '16px',
                                                minHeight: '60px',
                                            }}
                                        />
                                    )}
                                </Stack>
                            )}
                        </>
                    )}
                </Stack>
                <Divider
                    sx={{
                        borderColor: '#F4F4F6',
                    }}
                />
                <Stack
                    sx={{
                        flexDirection: 'row',
                        alignItems: 'center',
                        justifyContent: shouldShowReadAllButton ? 'space-between' : 'flex-end',
                        padding: '16px 16px 24px 16px',
                        gap: 1,
                    }}>
                    {shouldShowReadAllButton && (
                        <Button
                            variant="text"
                            sx={{
                                width: '50%',
                            }}
                            disabled={isPendingReadAll}
                            onClick={handleReadAllNotifications}>
                            Прочитать всё
                        </Button>
                    )}
                    <Button
                        onClick={handleOpenNotificationsPage}
                        variant="text"
                        sx={{
                            backgroundColor: '#EFF6FF',
                            width: 'calc(50% - 8px)',
                        }}>
                        Смотреть все
                    </Button>
                </Stack>
            </Stack>
        </RightSidebarWrapper>
    )
}
